/**
 * EventsOrgFilter — Smart Admin replacement R4 (2026-05-17).
 *
 * Typeahead picker for the Events list page's Organization filter.
 * Mirrors the identifier-aware search pattern from R2A's
 * EventOrganizationLocationSelector: looks up orgs by name OR
 * facility_id / promo_code / affiliate_id with sanitization of
 * PostgREST grammar chars.
 *
 * R9 (2026-05-25): the parent now persists only the org id in the URL
 * (was previously id + display label). When `value` is non-empty and no
 * label is in component state, fetch the org row by id so the display
 * chip stays accurate even after rename or page reload. Preserves the
 * orgDisplayLabel fallback chain (facility_id || promo_code ||
 * affiliate_id, with is_no_reseller short-circuit).
 */

import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../services/supabase";

interface OrgRow {
  id: string;
  name: string;
  facility_id?: string | null;
  promo_code?: string | null;
  affiliate_id?: string | null;
  is_no_reseller?: boolean;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
}

const orgDisplayLabel = (org: OrgRow): string => {
  if (org.is_no_reseller) {
    return org.name;
  }
  const ident = org.facility_id || org.promo_code || org.affiliate_id || "";
  return ident ? `${org.name} (${ident})` : org.name;
};

const EventsOrgFilter: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  // R9 (2026-05-25): selected-org lookup. Driven by `value` (id), runs only
  // when a value is set. Provides the display chip text without requiring
  // the parent to round-trip a label through the URL — which would go
  // stale on org rename.
  const { data: selectedOrg } = useQuery({
    queryKey: ["events-org-filter-selected", value],
    queryFn: async () => {
      if (!value) {
        return null;
      }
      const { data } = await supabase
        .from("organizations")
        .select(
          "id, name, facility_id, promo_code, affiliate_id, is_no_reseller",
        )
        .eq("id", value)
        .maybeSingle();
      return (data ?? null) as OrgRow | null;
    },
    enabled: !!value,
    staleTime: 5 * 60 * 1000,
  });
  const selectedLabel = selectedOrg ? orgDisplayLabel(selectedOrg) : "";

  const { data: results = [] } = useQuery({
    queryKey: ["events-org-filter", term],
    queryFn: async () => {
      const t = term.trim();
      // Sanitize PostgREST grammar chars per R1+R2 post-ship audit fix.
      const safe = t.replace(/[,()."*\\]/g, "").trim();

      let identifierMatches: OrgRow[] = [];
      if (safe) {
        const { data: id } = await supabase
          .from("organizations")
          .select(
            "id, name, facility_id, promo_code, affiliate_id, is_no_reseller",
          )
          .or(
            `facility_id.ilike.${safe}%,promo_code.ilike.${safe}%,affiliate_id.ilike.${safe}%`,
          )
          .eq("is_archived", false)
          .order("name", { ascending: true })
          .limit(10);
        identifierMatches = (id ?? []) as OrgRow[];
      }

      const nameQ = supabase
        .from("organizations")
        .select(
          "id, name, facility_id, promo_code, affiliate_id, is_no_reseller",
        )
        .eq("is_archived", false)
        .order("name", { ascending: true })
        .limit(20);
      if (t) {
        nameQ.ilike("name", `%${t}%`);
      }
      const { data: nameRows } = await nameQ;

      const seen = new Set<string>();
      const merged: OrgRow[] = [];
      const push = (rows: (OrgRow | null | undefined)[]) => {
        for (const o of rows) {
          if (o && !seen.has(o.id)) {
            seen.add(o.id);
            merged.push(o);
            if (merged.length >= 25) {
              break;
            }
          }
        }
      };
      push(identifierMatches);
      push((nameRows ?? []) as OrgRow[]);
      return merged;
    },
    staleTime: 30_000,
  });

  return (
    <div className="flex items-center gap-2 relative" ref={ref}>
      <label className="text-sm font-medium text-gray-700">Org:</label>
      <div className="relative">
        <div className="flex items-center gap-1 border border-gray-300 rounded-lg pl-2 pr-1 py-1 bg-white">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={value ? selectedLabel : term}
            onChange={(e) => {
              if (value) {
                onChange("");
              }
              setTerm(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="All orgs"
            className="px-1 py-0.5 text-sm w-56 outline-none"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setTerm("");
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="Clear org filter"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
            {results.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-500">No matches</p>
            )}
            {results.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => {
                  onChange(org.id);
                  setTerm("");
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                  org.is_no_reseller ? "bg-yellow-50" : ""
                }`}
              >
                {orgDisplayLabel(org)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsOrgFilter;
